import { Navigate, useParams, useSearchParams } from 'react-router-dom';

/**
 * Where the old addresses go.
 *
 * Merging valuation requests and assignments moved every one of their URLs.
 * People have these in bookmarks, in Teams messages and in the body of emails
 * to clients, and a 404 is a poor way to tell them a refactor happened - so
 * each old address still resolves, to the place its content now lives.
 *
 * The rewrite is not always a constant, which is why these are components
 * rather than `<Navigate>` elements. The four work screens used to identify
 * their subject with an `?assignmentId=` query param and now identify it with
 * a path segment, so the redirect has to read the query to know where to send
 * the visitor.
 *
 * These are cheap to keep and easy to delete. When the analytics show nobody
 * arriving on them any more, the whole file goes.
 */

/**
 * `/home/my-assignments/<tab>?assignmentId=X` -> `/assignments/X/<tab>`.
 *
 * Without an id there is nothing to open, so it falls back to the list - the
 * same thing the old screen did when the param was missing, except that it
 * says so rather than silently picking the first record.
 */
export function LegacyWorkScreenRedirect({ tab }: { tab: 'tasks' | 'files' | 'inspection' | 'comments' }) {
  const [searchParams] = useSearchParams();

  const assignmentId = searchParams.get('assignmentId');
  const rest = new URLSearchParams(searchParams);
  rest.delete('assignmentId');

  const query = rest.toString();

  if (!assignmentId) {
    // The cross-record queues live at these addresses now.
    const listing = tab === 'tasks' || tab === 'files' ? `/assignments/${tab}` : '/assignments';

    return <Navigate to={query ? `${listing}?${query}` : listing} replace />;
  }

  const target = `/assignments/${encodeURIComponent(assignmentId)}/${tab}`;

  return <Navigate to={query ? `${target}?${query}` : target} replace />;
}

/** `/sg/ivs/:ivid` -> `/assignments/:ivid`, and `/sg/ivs` -> `/assignments`. */
export function LegacyValuationRedirect() {
  const { ivid } = useParams<{ ivid: string }>();
  const [searchParams] = useSearchParams();

  const query = searchParams.toString();
  const target = ivid ? `/assignments/${encodeURIComponent(ivid)}` : '/assignments';

  return <Navigate to={query ? `${target}?${query}` : target} replace />;
}
